import { Box, Button } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  oktaKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import {
  DISABLE_PASSWORD_REVEAL_IN_OKTA_CONTROL,
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import LoadingIcon from "../LoadingIcon";
import { Toast } from "../Toast";

export default function DisablePasswordRevealInOktaButton({
  controlId = null,
  appIds = [],
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { disablePasswordRevealInOkta, trackEvent, checkOktaManagementScopes } =
    useAPI();
  const [
    isDisablePasswordRevealDialogOpen,
    setIsDisablePasswordRevealDialogOpen,
  ] = useState(false);
  const [isDisablingPasswordReveal, setIsDisablingPasswordReveal] =
    useState(false);

  const { permitted: hasUserPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.DISABLE_PASSWORD_REVEAL_IN_OKTA
  );

  const {
    data: oktaRemediationPerms,
    isLoading: isCheckingRemediationPermissions,
  } = useQuery({
    queryKey: oktaKeys.remediationPermissions(),
    queryFn: async () => {
      const response = await checkOktaManagementScopes();
      return response.data.length === 0;
    },
  });
  const hasRemediationPermissions = oktaRemediationPerms ?? false;

  const hasAllPermissions = hasUserPermission && hasRemediationPermissions;
  const isButtonDisabled =
    disabled ||
    !hasAllPermissions ||
    isCheckingRemediationPermissions ||
    isDisablingPasswordReveal ||
    appIds.length <= 0;

  const handleDisablePasswordReveal = async () => {
    const message = "User clicked on 'Disable password reveal in Okta' button";
    trackEvent(message).then();
    setIsDisablePasswordRevealDialogOpen(true);
  };

  const handleDisablePasswordRevealConfirmButton = async () => {
    const message =
      "User clicked on 'Confirm' button for disabling password reveal in Okta";
    trackEvent(message).then();
    Toast.info(`Disabling password reveal in Okta...`);

    try {
      if (appIds) {
        handleCloseDialog();
        setIsDisablingPasswordReveal(true);
        const hiddenPasswordCountData = await disablePasswordRevealInOkta(
          controlId,
          appIds,
          DISABLE_PASSWORD_REVEAL_IN_OKTA_CONTROL
        );
        const hiddenPasswordCount = hiddenPasswordCountData.data;
        const expectedHiddenPasswordCount = appIds.length;
        if (hiddenPasswordCount === expectedHiddenPasswordCount) {
          Toast.success(
            `Successfully hidden password for ${hiddenPasswordCount}/${expectedHiddenPasswordCount} apps`
          );
        } else if (hiddenPasswordCount > 0) {
          Toast.info(
            `Hidden password for ${hiddenPasswordCount}/${expectedHiddenPasswordCount} apps`
          );
        } else {
          Toast.error(
            "Unable to disable password reveal in Okta. Please contact support for assistance"
          );
        }

        // Refresh the misconfigurations table
        queryClient.invalidateQueries({
          queryKey: controlKeys.all,
        });
        if (controlId) {
          queryClient.invalidateQueries({
            queryKey: remediationAuditLogKeys.auditLogs(controlId),
          });
        }
        queryClient.invalidateQueries({
          queryKey: remediationKeys.all,
        });
      }
    } catch (error) {
      Toast.error("Failed to disable password reveal in Okta");
    } finally {
      setIsDisablingPasswordReveal(false);
    }
  };

  const handleCloseDialog = () => {
    setIsDisablePasswordRevealDialogOpen(false);
  };

  const getTooltipMessage = () => {
    if (!showToolTip) return "";
    if (!hasUserPermission) return VIEWER_ACCESS_DENIED_MESSAGE;
    if (!hasRemediationPermissions) {
      return "Okta remediation permissions are required. Please configure them in the Okta integration settings.";
    }
    if (isCheckingRemediationPermissions) {
      return "Checking permissions...";
    }
    if (isDisablingPasswordReveal) {
      return "Disabling password reveal...";
    }
    return "Disable password reveal for selected apps";
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={getTooltipMessage()}
        permission={hasUserPermission}
        displayPermissionDeniedMessage={false}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleDisablePasswordReveal}
            disabled={isButtonDisabled}
          >
            <Box
              style={{
                visibility: isDisablingPasswordReveal ? "hidden" : "visible",
              }}
            >
              <SvgToIcon
                IconLogo={icons.DisablePasswordRevealInOktaIcon}
                style={{
                  width: "14px",
                  color: isButtonDisabled
                    ? colors.action_disabled
                    : colors.icon_action_active,
                }}
              />
            </Box>
            {isDisablingPasswordReveal && (
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              >
                <LoadingIcon type="circular" size={14} />
              </Box>
            )}
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      {/* Restrict confirmation dialog */}
      <ConfirmationDialog
        openDialog={isDisablePasswordRevealDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleDisablePasswordRevealConfirmButton}
        dialogTitle="Disable password reveal in Okta"
        dialogContentFirstLine="Are you sure you want to disable password reveal for these apps?"
        dialogContentSecondLine="The password will no longer be visible in Okta."
        confirmationButtonText="Disable Password Reveal"
        destructive={true}
      />
    </>
  );
}
